import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe } from 'lucide-react';
import AdvisorSelect from '@components/AdvisorSelect';
import NewCustomerButton from '@components/NewCustomerButton';
import { useSessionStore } from '@stores/sessionStore';
import { useT } from '@lib/i18n';
import type { Advisor, Language } from '@lib/types';

const PHONE_REGEX = /^(\+968)?\d{8}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizePhone = (raw: string): string => raw.replace(/[\s\-()]/g, '');

interface FormState {
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  advisorId: string;          // B.4 — advisors.json id
}

type FieldKey = keyof FormState;
type FormErrors = Partial<Record<FieldKey, string>>;

export default function Step1Welcome() {
  const t = useT();
  const navigate = useNavigate();
  const language = useSessionStore((s) => s.language);
  const startSession = useSessionStore((s) => s.startSession);
  const setEmail = useSessionStore((s) => s.setEmail);

  const [form, setForm] = useState<FormState>({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    advisorId: '',
  });
  const [advisor, setAdvisor] = useState<Advisor | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('lang', language);
    root.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
  }, [language]);

  const updateField = (key: FieldKey, value: string) => {
    setForm((s) => ({ ...s, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const switchLanguage = (lang: Language) => {
    if (lang !== language) useSessionStore.setState({ language: lang });
  };

  const validate = (): FormErrors => {
    const next: FormErrors = {};
    if (!form.clientName.trim()) next.clientName = t('welcome.errClientName');
    const phone = normalizePhone(form.clientPhone);
    if (!PHONE_REGEX.test(phone)) next.clientPhone = t('welcome.errPhone');
    const email = form.clientEmail.trim();
    if (email && !EMAIL_REGEX.test(email)) next.clientEmail = t('welcome.errEmail');
    if (!form.advisorId || !advisor) next.advisorId = t('welcome.errAdvisor');
    return next;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0 || !advisor) return;

    const phone = normalizePhone(form.clientPhone);
    // Display name follows the session language. The PDF + WhatsApp
    // template are AR-only, so the Worker uses the AR name from the
    // sessionSnapshot.advisorName when language is AR. When language is
    // EN we still send AR through to respond.io (template body var
    // {{3}} is Arabic by approval).
    const displayName = advisor.name[language];
    startSession({
      clientName: form.clientName.trim(),
      clientPhone: phone,
      language,
      advisorId: advisor.id,
      advisorName: displayName,
      advisorRespondId: advisor.respondId,
    });
    const email = form.clientEmail.trim();
    if (email) setEmail(email);

    navigate('/project');
  };

  const handleAdvisorChange = (next: Advisor | null) => {
    setAdvisor(next);
    setForm((s) => ({ ...s, advisorId: next?.id ?? '' }));
    if (errors.advisorId) setErrors((e) => ({ ...e, advisorId: undefined }));
  };

  const isAr = language === 'ar';

  return (
    <div className="step-container">
      <div className="flex items-center justify-between mb-6 gap-3">
        <span className="inline-flex items-center gap-2 text-xs text-navy-400">
          <span className="w-1.5 h-1.5 rounded-full bg-gold" />
          {t('welcome.timeBudget')}
          <span className="num-en">· 1 {t('common.minute')}</span>
        </span>
        <div className="flex items-center gap-2">
          {/* V3 Day 3.0 — replaced the V2.5 back-home link; the */}
          {/* "new customer" button now handles return-to-splash. */}
          <NewCustomerButton />
          <LanguageToggle current={language} onChange={switchLanguage} />
        </div>
      </div>

      <header className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-navy mb-2">
          {t('welcome.title')}
        </h1>
        <p className="text-navy-600">{t('welcome.subtitle')}</p>
      </header>

      <form onSubmit={handleSubmit} className="card p-6 md:p-8 space-y-5" noValidate>
        <Field
          id="clientName"
          label={t('welcome.clientName')}
          required
          value={form.clientName}
          onChange={(v) => updateField('clientName', v)}
          placeholder={t('welcome.clientNamePh')}
          error={errors.clientName}
          autoComplete="name"
        />

        <Field
          id="clientPhone"
          label={t('welcome.phone')}
          required
          value={form.clientPhone}
          onChange={(v) => updateField('clientPhone', v)}
          placeholder={t('welcome.phonePh')}
          error={errors.clientPhone}
          hint={t('welcome.phoneHint')}
          autoComplete="tel"
          inputMode="tel"
          dir="ltr"
          textAlign={isAr ? 'right' : 'left'}
        />

        <Field
          id="clientEmail"
          label={t('welcome.email')}
          optionalLabel={t('common.optional')}
          value={form.clientEmail}
          onChange={(v) => updateField('clientEmail', v)}
          placeholder={t('welcome.emailPh')}
          error={errors.clientEmail}
          autoComplete="email"
          inputMode="email"
          type="email"
          dir="ltr"
          textAlign={isAr ? 'right' : 'left'}
        />

        <div>
          <label
            htmlFor="advisorId"
            className="block text-sm font-medium text-navy mb-1.5"
          >
            {t('welcome.advisor')}
            <span className="text-sold ms-1">*</span>
          </label>
          <AdvisorSelect
            id="advisorId"
            value={form.advisorId}
            onChange={handleAdvisorChange}
            language={language}
            placeholder={t('welcome.advisorPh')}
            invalid={Boolean(errors.advisorId)}
            required
          />
          {errors.advisorId && (
            <p id="advisorId-err" className="mt-1 text-xs text-sold">
              {errors.advisorId}
            </p>
          )}
        </div>

        <div className="pt-2">
          <button type="submit" className="btn-primary w-full md:w-auto md:px-10">
            {t('welcome.startBtn')}
          </button>
        </div>
      </form>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  optionalLabel?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'tel' | 'email';
  type?: 'text' | 'email' | 'tel';
  dir?: 'ltr' | 'rtl';
  textAlign?: 'left' | 'right';
}

function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  error,
  hint,
  required,
  optionalLabel,
  autoComplete,
  inputMode = 'text',
  type = 'text',
  dir,
  textAlign,
}: FieldProps) {
  const errId = `${id}-err`;
  const hintId = `${id}-hint`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-navy mb-1.5">
        {label}
        {required && <span className="text-sold ms-1">*</span>}
        {!required && optionalLabel && (
          <span className="ms-2 text-xs text-navy-400">({optionalLabel})</span>
        )}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        dir={dir}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errId : hint ? hintId : undefined}
        style={textAlign ? { textAlign } : undefined}
        className={[
          'w-full px-3 py-2.5 rounded-lg border bg-white',
          'text-navy placeholder:text-navy-300',
          'transition-colors duration-150',
          'focus:outline-none focus:ring-2 focus:ring-gold/40',
          error ? 'border-sold focus:border-sold' : 'border-navy-100 focus:border-gold',
        ].join(' ')}
      />
      {error ? (
        <p id={errId} className="mt-1 text-xs text-sold">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="mt-1 text-xs text-navy-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

interface LanguageToggleProps {
  current: Language;
  onChange: (lang: Language) => void;
}

function LanguageToggle({ current, onChange }: LanguageToggleProps) {
  return (
    <div
      role="group"
      aria-label="Language"
      className="inline-flex items-center gap-1 p-1 bg-navy-50 rounded-full text-xs"
    >
      <Globe className="w-3.5 h-3.5 text-navy-400 ms-1.5" />
      <ToggleBtn active={current === 'ar'} onClick={() => onChange('ar')} label="العربية" />
      <ToggleBtn active={current === 'en'} onClick={() => onChange('en')} label="English" />
    </div>
  );
}

function ToggleBtn({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'px-3 py-1 rounded-full transition-colors',
        active ? 'bg-navy text-gold' : 'text-navy-600 hover:text-navy',
      ].join(' ')}
    >
      {label}
    </button>
  );
}
